import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

export const Reveal = ({ children, delay = 0, y = 44, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-90px" }}
    transition={{ duration: 0.85, ease: EASE, delay }}
  >
    {children}
  </motion.div>
);

export const SectionHeading = ({ chapter, title, kicker }) => (
  <div className="mb-16 md:mb-24">
    <Reveal>
      <div className="font-mono-data text-[#b88457] text-xs md:text-sm tracking-[0.3em] mb-6 flex items-center gap-4">
        <span className="inline-block w-10 h-px bg-[#b88457]" />
        {chapter}
      </div>
    </Reveal>
    <Reveal delay={0.08}>
      <h2 className="font-display font-black uppercase tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.92] max-w-4xl">
        {title}
      </h2>
    </Reveal>
    {kicker && (
      <Reveal delay={0.16}>
        <p className="mt-6 max-w-xl text-zinc-400 text-base leading-relaxed">{kicker}</p>
      </Reveal>
    )}
  </div>
);

export const ConfidenceBar = ({ value, danger, primary }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const color = danger ? "#c0453a" : primary ? "#b88457" : "#71717a";
  return (
    <div ref={ref} className="w-full">
      <div className="h-[3px] w-full bg-white/10 overflow-hidden">
        <motion.div
          className="h-full"
          style={{ background: color, boxShadow: primary ? "0 0 16px rgba(184,132,87,0.6)" : "none" }}
          initial={{ width: 0 }}
          animate={inView ? { width: `${value}%` } : {}}
          transition={{ duration: 1.5, ease: EASE }}
        />
      </div>
    </div>
  );
};

export const Counter = ({ to, decimals = 0, suffix = "", duration = 2.2, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start;
    let raf;
    const step = (t) => {
      if (!start) start = t;
      const p = Math.min((t - start) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(eased * to);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration]);
  return (
    <span ref={ref} className={className}>
      {val.toFixed(decimals)}
      {suffix}
    </span>
  );
};
